import React, { useEffect, useState } from 'react';
import { ModalProps, Zoom } from '@mui/material';
import { tap } from 'rxjs/operators';
import { AuthService } from '../../../auth/services/auth.service';
import useService from '../../../injector/hooks/useService';
import { useSubscribe } from '../../../core/hooks/useSubscribe';
import { HdHoverTracker } from '../../../analytics/components/hover-tracker/HoverTracker';
import { ClipboardTextareaContainer } from '../../../clipboard/hooks/useClipboardService';
import { StyledHdModal } from './StyledModal';

export interface BaseModalProps extends ModalProps {
  children: React.ReactElement;
  open: boolean;
  transitionTime?: number;
  placement?: 'center' | 'top';
  variant?: 'primary' | 'custom';
  className?: string;
  contentClassName?: string;
  styling?: {};
  TransitionComponent?: React.JSXElementConstructor<any>;
  onClose?: (event: object, reason: string) => void;
  transitionEndListener?: Function;
  dialogHoverTrackingAction?: string;
  dataId?: string;
}

export default function BaseModal(props: BaseModalProps) {
  const {
    children,
    TransitionComponent = Zoom,
    transitionTime = 300,
    open,
    placement = 'center',
    className,
    transitionEndListener,
    dialogHoverTrackingAction,
    variant = 'primary',
    contentClassName,
    styling,
    ...rest
  } = props;
  const [openModal, setOpenModal] = useState(open);
  const alignmentClass = `${placement}-align`;
  const [contentElem, setContentElem] = useState(null);
  const authService = useService(AuthService);

  useEffect(() => {
    setOpenModal(open);
  }, [open]);

  useSubscribe(authService.beforeLogoutSubject.pipe(tap(() => {
    if (openModal) {
      setOpenModal(false);
    }
  })), []);

  return (
    <StyledHdModal open={openModal} className={`${className} ${alignmentClass}`} {...rest}>
      <TransitionComponent
        appear
        in={openModal}
        timeout={transitionTime}
        role='presentation'
        addEndListener={transitionEndListener}
      >
        <HdHoverTracker trackingAction={dialogHoverTrackingAction}>
          <div
            ref={elem => {
              setContentElem(elem);
            }}
            className={`${variant} ${contentClassName}`}
            style={{ ...styling }}
          >
            <ClipboardTextareaContainer.Provider value={contentElem}>
              {children}
            </ClipboardTextareaContainer.Provider>
          </div>
        </HdHoverTracker>
      </TransitionComponent>
    </StyledHdModal>
  );
}
